import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { esObligatorio, esVisible, leer, validar, zRespuesta, type Campo, type EsquemaFormulario, type Hallazgo, type Moneda } from '@rumbo/form'
import { api } from '@/servicios/api'
import { Boton, Entrada, Espera, MensajeError, Seleccion, Tarjeta } from '@/componentes/ui'
import { LockupMPPT } from '@/marca/LockupMPPT'
import { CampoFormulario } from './CampoFormulario'
import { GeoCascada, type ValorGeo } from './GeoCascada'
import { SelectorOrigen, type ValorOrigen } from './SelectorOrigen'
import { TramosEditor, type TramoUI } from './TramosEditor'
import { encolar, listarCola, quitarDeCola } from '@/servicios/cola'

interface InfoInstitucion {
  id: string
  nombre: string
  codParroquia: string
  parroquia: string
  municipio: string
  estado: string
}
interface InfoEnlace {
  abierto: boolean
  motivo: string | null
  mensaje: string | null
  campana: { id: string; formularioVersion: string }
  institucion: InfoInstitucion
  esquema: EsquemaFormulario | null
  restantes: number
}

type Datos = Record<string, unknown>

function escribir(datos: Datos, ruta: string, valor: unknown): Datos {
  const partes = ruta.split('.')
  const raiz: Datos = { ...datos }
  let cursor: Datos = raiz
  for (let i = 0; i < partes.length - 1; i++) {
    const k = partes[i]!
    cursor[k] = { ...((cursor[k] as Datos) ?? {}) }
    cursor = cursor[k] as Datos
  }
  cursor[partes.at(-1)!] = valor
  return raiz
}

const uuid = () => crypto.randomUUID()

// En el modo QR la institución la fija el enlace: institucionId y su ubicación
// (geo_cascada) no se piden — se muestran como dato fijo (ver PASO estudiante).
const CAMPOS_OCULTOS_QR = new Set(['idOrganismo', 'institucionId', 'institucionUbicacion'])

export function PaginaEncuesta() {
  const { token = '' } = useParams()
  const [info, setInfo] = useState<InfoEnlace | 'cargando' | 'error'>('cargando')
  const [paso, setPaso] = useState(0)
  const [datos, setDatos] = useState<Datos>({})
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [enviando, setEnviando] = useState(false)
  const [folio, setFolio] = useState<string | null>(null)
  const [pendienteOffline, setPendienteOffline] = useState(false)
  const idRespuesta = useRef(uuid())
  const inicioRef = useRef(new Date().toISOString())

  useEffect(() => {
    api<InfoEnlace>(`/publico/enlaces/${token}`)
      .then(setInfo)
      .catch(() => setInfo('error'))
  }, [token])

  // Reintenta lo que haya quedado en la cola al montar y al recuperar señal
  // (p. ej. se cerró la app sin conexión, o se abrió el enlace en modo avión).
  useEffect(() => {
    const drenar = () => {
      listarCola().then((cola) => {
        for (const item of cola) {
          if (item.estado === 'pendiente' && item.token === token) {
            api(`/publico/enlaces/${item.token}/respuestas`, { metodo: 'POST', cuerpo: item.datos })
              .then(() => quitarDeCola(item.id))
              .catch(() => {})
          }
        }
      })
    }
    drenar()
    window.addEventListener('online', drenar)
    return () => window.removeEventListener('online', drenar)
  }, [token])

  // Todos los hooks van ANTES de cualquier retorno anticipado (reglas de los Hooks):
  // el número de hooks llamados no puede cambiar entre el estado "cargando" y el cargado.
  const infoLista = typeof info === 'object' ? info : null
  const esquema = infoLista?.esquema ?? null
  const pasos = esquema?.pasos ?? []
  const enPaso = pasos[paso]
  const camposVisibles = useMemo(
    () => (enPaso ? enPaso.campos.filter((c) => !CAMPOS_OCULTOS_QR.has(c.id) && esVisible(c, datos, 'autoaplicada_qr')) : []),
    [enPaso, datos],
  )
  const esUltimo = pasos.length > 0 && paso === pasos.length - 1
  const esPrimero = paso === 0

  const armarRespuesta = useCallback((): unknown => {
    if (!infoLista || !esquema) return null
    const geo = (datos.geo as ValorGeo) ?? {}
    const origen = (datos.origen as ValorOrigen) ?? {}
    const tramosIda = ((datos.tramos as Datos)?.ida as TramoUI[]) ?? []
    const tramosRegreso = ((datos.tramos as Datos)?.regreso as TramoUI[]) ?? []
    return {
      id: idRespuesta.current,
      campanaId: infoLista.campana.id,
      formularioVersion: infoLista.campana.formularioVersion,
      modoCaptura: 'autoaplicada_qr',
      informante: datos.informante,
      parentesco: datos.parentesco,
      consentimiento: { aceptado: true, version: esquema.consentimiento.version, aceptadoEn: new Date().toISOString() },
      asentimiento: datos.asentimiento,
      nombre: datos.nombre || undefined,
      edad: datos.edad,
      // Género obligatorio (schema v1.1). El esquema del formulario los pide
      // en el paso Estudiante y el state los captura, pero armarRespuesta
      // tenía que incluirlos explícitamente — sin esto el payload falla
      // validación con 'Faltan datos: genero'.
      genero: datos.genero,
      discapacidad: datos.discapacidad,
      nivel: datos.nivel,
      nivelOtro: datos.nivelOtro,
      // Grado estructurado (v1.1). El esquema del formulario usa 'grado.tipo'
      // y 'grado.numero', que escribir() guarda bajo datos.grado = {tipo, numero}.
      // Si no vino el tipo (campo opcional), omitimos todo el objeto.
      grado: (datos.grado as { tipo?: string; numero?: number } | undefined)?.tipo
        ? { tipo: (datos.grado as { tipo: string }).tipo, numero: Number((datos.grado as { numero: number }).numero) }
        : undefined,
      institucionId: infoLista.institucion.id,
      lugarInicio: datos.lugarInicio,
      lugarInicioOtro: datos.lugarInicioOtro,
      origen: {
        codParroquia: geo.codParroquia ?? infoLista.institucion.codParroquia,
        localidad: (datos.origenLocalidad as string) || infoLista.institucion.municipio,
        sectorId: origen.sectorId,
        sectorTexto: origen.sectorTexto,
        celda: origen.celda,
        punto: origen.punto,
        metodo: origen.metodo ?? 'lista',
      },
      // Destino override: solo lo mandamos si el usuario marcó 'destino_distinto'
      // y rellenó la cascada. Si no, el backend deriva de la institución.
      ...(datos.destinoDistinto && (datos.destinoGeo as ValorGeo | undefined)?.codParroquia
        ? {
            destino: {
              codEstado: (datos.destinoGeo as ValorGeo).codEstado!,
              codMunicipio: (datos.destinoGeo as ValorGeo).codMunicipio!,
              codParroquia: (datos.destinoGeo as ValorGeo).codParroquia!,
            },
          }
        : {}),
      diasSemana: datos.diasSemana,
      horaSalidaIda: datos.horaSalidaIda,
      horaLlegadaIda: datos.horaLlegadaIda,
      modoIda: datos.modoIda,
      modoIdaOtro: datos.modoIdaOtro,
      regresoIgualIda: datos.regresoIgualIda,
      horaSalidaRegreso: datos.horaSalidaRegreso,
      horaLlegadaRegreso: datos.horaLlegadaRegreso,
      modoRegreso: datos.regresoIgualIda ? datos.modoIda : datos.modoRegreso,
      modoRegresoOtro: datos.modoRegresoOtro,
      tramos: [
        ...tramosIda.map((t) => ({ ...t, sentido: 'ida' as const })),
        ...(datos.regresoIgualIda ? [] : tramosRegreso.map((t) => ({ ...t, sentido: 'regreso' as const }))),
      ],
      gastoIda: datos.gastoIda,
      gastoRegreso: datos.gastoRegreso,
      tarifaEstudiantil: datos.tarifaEstudiantil,
      declarados: datos.declarados ?? {},
      // El UI expone un solo multi-select `dificultades` (1 a 3). Al armar el
      // payload, la primera elegida va a dificultadPrincipal y el resto a
      // dificultadesOtras — esto evita cambiar zRespuesta y los agregados.
      dificultadPrincipal: Array.isArray(datos.dificultades) ? (datos.dificultades as string[])[0] : datos.dificultadPrincipal,
      dificultadOtro: datos.dificultadOtro,
      dificultadesOtras: Array.isArray(datos.dificultades)
        ? (datos.dificultades as string[]).slice(1)
        : (datos.dificultadesOtras ?? []),
      capturadoEn: inicioRef.current,
    }
  }, [infoLista, esquema, datos])

  const hallazgos: Hallazgo[] = useMemo(() => {
    if (!esUltimo) return []
    const payload = armarRespuesta()
    if (!payload) return []
    try {
      return validar(payload as never)
    } catch {
      return []
    }
  }, [esUltimo, armarRespuesta])

  const validarPaso = useCallback((): boolean => {
    if (!enPaso) return false
    const nuevosErrores: Record<string, string> = {}
    for (const c of camposVisibles) {
      if (c.id === 'origen') continue
      const obligatorio = esObligatorio(c, datos, 'autoaplicada_qr')
      const valor = leer(datos, c.id)
      if (obligatorio && (valor === undefined || valor === '' || valor === null)) {
        nuevosErrores[c.id] = 'Este dato es obligatorio.'
        continue
      }
      // Rango de enteros: el atributo HTML `max` solo colorea el borde, no
      // impide escribir valores inválidos. Hay que validar en JS o el payload
      // se arma mal (visto con declarados.diasMes > 31 → "Faltan datos" en el
      // último paso, muy confuso para el usuario).
      if (c.tipo === 'entero' && typeof valor === 'number') {
        if (c.min !== undefined && valor < c.min) nuevosErrores[c.id] = `Debe ser ${c.min} como mínimo.`
        else if (c.max !== undefined && valor > c.max) nuevosErrores[c.id] = `Debe ser ${c.max} como máximo.`
      }
      // Multi-select: cuando es obligatorio con rango (ej. 'dificultades':
      // 1 a 3), bloquea avanzar si el usuario no eligió al menos el mínimo.
      if (c.tipo === 'multiple' && Array.isArray(valor)) {
        if (c.min !== undefined && valor.length < c.min) nuevosErrores[c.id] = `Elija al menos ${c.min} opción(es).`
        else if (c.max !== undefined && valor.length > c.max) nuevosErrores[c.id] = `Elija como máximo ${c.max} opción(es).`
      }
    }
    if (enPaso.id === 'origen') {
      const origen = datos.origen as ValorOrigen | undefined
      if (!origen?.sectorId && !origen?.celda && !origen?.sectorTexto) nuevosErrores.origen = 'Indique su sector, en la lista, el mapa o escribiéndolo.'
    }
    setErrores(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }, [enPaso, camposVisibles, datos])

  async function enviar() {
    if (!validarPaso()) return
    const payload = armarRespuesta()
    const parseo = zRespuesta.safeParse(payload)
    if (!parseo.success) {
      // Mensaje con qué campos específicos están fallando. El genérico anterior
      // ("Revise los pasos anteriores") deja al usuario sin pista para encontrar
      // el error — típicamente es un campo oculto por modoCaptura que
      // armarRespuesta no setea pero el schema igual exige. Se logea a consola
      // el detalle completo para diagnóstico; al usuario se muestran máximo 5.
      console.warn('[encuesta] payload rechazado por zRespuesta:', parseo.error.issues, { payload })
      const campos = parseo.error.issues
        .map((i) => i.path.filter((p) => typeof p === 'string' || typeof p === 'number').join('.'))
        .filter(Boolean)
      const unicos = Array.from(new Set(campos)).slice(0, 5)
      const texto = unicos.length
        ? `Faltan datos o hay valores inválidos en: ${unicos.join(', ')}.`
        : 'Faltan datos por completar. Revise los pasos anteriores.'
      setErrores({ general: texto })
      return
    }
    setEnviando(true)
    try {
      const r = await api<{ folio: string }>(`/publico/enlaces/${token}/respuestas`, { metodo: 'POST', cuerpo: parseo.data })
      setFolio(r.folio)
    } catch {
      // Sin conexión (u otro error de red): se guarda localmente y se reintenta solo.
      await encolar({ id: idRespuesta.current, token, datos: parseo.data, estado: 'pendiente', intentos: 0, guardadoEn: new Date().toISOString() })
      setPendienteOffline(true)
      setFolio('PENDIENTE')
    } finally {
      setEnviando(false)
    }
  }

  if (info === 'cargando') {
    return (
      <PantallaCentrada>
        <Espera etiqueta="Cargando la encuesta…" />
      </PantallaCentrada>
    )
  }
  if (info === 'error') return <PantallaMensaje titulo="No pudimos abrir la encuesta" texto="Verifique su conexión o el código QR e intente de nuevo." />
  if (!info.abierto) return <PantallaMensaje titulo="Esta encuesta no está disponible" texto={info.mensaje ?? 'Intente más tarde.'} />
  if (folio) return <PantallaGracias folio={folio} offline={pendienteOffline} />
  if (!enPaso) return null

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-4 pb-28 pt-6">
      <header className="mb-6 flex items-center justify-between">
        <LockupMPPT ancho={140} />
      </header>

      <Tarjeta className="p-5 sm:p-7">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-texto-tenue">
          {info.institucion.nombre} · Paso {paso + 1} de {pasos.length}
        </p>
        <h1 className="mb-1 text-2xl font-bold text-azul">{enPaso.titulo}</h1>
        {enPaso.descripcion && <p className="mb-5 text-sm text-texto-suave">{enPaso.descripcion}</p>}

        <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-superficie-alt">
          <div className="h-full rounded-full bg-oro transition-[width] duration-300" style={{ width: `${((paso + 1) / pasos.length) * 100}%` }} />
        </div>

        {paso === 0 && <PasoConsentimiento esquema={esquema!} datos={datos} setDatos={setDatos} errores={errores} />}

        <div className="space-y-6">
          {paso !== 0 && enPaso.id === 'estudiante' && (
            <div className="rounded-[var(--radius-rumbo)] bg-superficie-alt p-3.5 text-sm">
              <span className="text-texto-suave">Institución educativa: </span>
              <span className="font-medium text-texto">{info.institucion.nombre}</span>
              <span className="block text-texto-tenue">
                {info.institucion.parroquia}, {info.institucion.municipio}, {info.institucion.estado}
              </span>
            </div>
          )}
          {paso !== 0 && camposVisibles.map((c) => <CampoRenderer key={c.id} campo={c} datos={datos} setDatos={setDatos} error={errores[c.id]} />)}
          {paso !== 0 && enPaso.id === 'origen' && (
            <BloqueDestinoAlternativo
              datos={datos}
              setDatos={setDatos}
              nombreInstitucion={info.institucion.nombre}
              localidadInstitucion={`${info.institucion.parroquia}, ${info.institucion.municipio}, ${info.institucion.estado}`}
            />
          )}
        </div>

        {esUltimo && hallazgos.some((h) => h.severidad === 'advertencia') && (
          <div className="mt-6 rounded-[var(--radius-rumbo)] bg-advertencia-fondo p-4 text-sm text-advertencia">
            <p className="mb-1 font-medium">Antes de enviar, revise:</p>
            <ul className="list-inside list-disc space-y-0.5">
              {hallazgos
                .filter((h) => h.severidad === 'advertencia')
                .map((h, i) => (
                  <li key={i}>{h.mensaje}</li>
                ))}
            </ul>
          </div>
        )}
        {errores.general && <MensajeError>{errores.general}</MensajeError>}
      </Tarjeta>

      <div className="fixed inset-x-0 bottom-0 border-t border-borde bg-superficie/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-2xl gap-3">
          {!esPrimero && (
            <Boton variante="fantasma" onClick={() => setPaso((p) => p - 1)} className="flex-1">
              Atrás
            </Boton>
          )}
          <Boton
            className="flex-1"
            cargando={enviando}
            onClick={() => {
              if (paso === 0 && !(datos.consentimiento as boolean)) return setErrores({ consentimiento: 'Debe aceptar para continuar.' })
              if (!validarPaso()) return
              if (esUltimo) void enviar()
              else setPaso((p) => p + 1)
            }}
          >
            {esUltimo ? 'Enviar respuestas' : 'Continuar'}
          </Boton>
        </div>
      </div>
    </div>
  )
}

function CampoRenderer({ campo, datos, setDatos, error }: { campo: Campo; datos: Datos; setDatos: (fn: (d: Datos) => Datos) => void; error?: string }) {
  const cambiar = (v: unknown) => setDatos((d) => escribir(d, campo.id, v))

  if (campo.id === 'origen') {
    const geo = (datos.geo as ValorGeo) ?? {}
    return (
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 text-[15px] font-medium text-texto">Estado, municipio y parroquia de origen</p>
          <GeoCascada valor={geo} onCambio={(v) => setDatos((d) => escribir(d, 'geo', v))} />
        </div>
        <div>
          <p className="mb-1.5 text-[15px] font-medium text-texto">
            {campo.etiqueta}
            <span className="ml-1 text-rojo">*</span>
          </p>
          {campo.ayuda && <p className="mb-2 text-sm text-texto-suave">{campo.ayuda}</p>}
          <SelectorOrigen codParroquia={geo.codParroquia ?? null} valor={datos.origen as ValorOrigen} onCambio={(v) => setDatos((d) => escribir(d, 'origen', v))} />
          <MensajeError>{error}</MensajeError>
        </div>
      </div>
    )
  }

  if (campo.id === 'declarados.gastoMensual') {
    return <CampoGastoMensual datos={datos} setDatos={setDatos} />
  }

  if (campo.id.startsWith('tramos.')) {
    const sentido = campo.id.endsWith('ida') ? 'ida' : 'regreso'
    if (sentido === 'regreso' && datos.regresoIgualIda) return null
    return (
      <div>
        <p className="mb-1.5 text-[15px] font-medium text-texto">{campo.etiqueta}</p>
        {campo.ayuda && <p className="mb-2 text-sm text-texto-suave">{campo.ayuda}</p>}
        <TramosEditor tramos={((datos.tramos as Datos)?.[sentido] as TramoUI[]) ?? []} onCambio={(t) => setDatos((d) => escribir(d, `tramos.${sentido}`, t))} />
      </div>
    )
  }

  return <CampoFormulario campo={campo} valor={leer(datos, campo.id)} onCambio={cambiar} error={error} obligatorio={esObligatorio(campo, datos, 'autoaplicada_qr')} />
}

function PasoConsentimiento({
  esquema,
  datos,
  setDatos,
  errores,
}: {
  esquema: EsquemaFormulario
  datos: Datos
  setDatos: (fn: (d: Datos) => Datos) => void
  errores: Record<string, string>
}) {
  return (
    <div className="space-y-5">
      <p className="text-sm leading-relaxed text-texto">{esquema.objetivo}</p>
      <div className="rounded-[var(--radius-rumbo)] bg-info-fondo p-4 text-sm text-info">{esquema.consentimiento.texto}</div>
      <label className="objetivo-tactil foco-visible flex cursor-pointer items-start gap-3 rounded-[var(--radius-rumbo)] border border-borde-fuerte p-4">
        <input
          type="checkbox"
          checked={Boolean(datos.consentimiento)}
          onChange={(e) => setDatos((d) => ({ ...d, consentimiento: e.target.checked }))}
          className="mt-0.5 size-5 accent-[var(--mppt-azul-marino)]"
        />
        <span className="text-sm font-medium text-texto">Acepto participar en la encuesta</span>
      </label>
      <MensajeError>{errores.consentimiento}</MensajeError>

      <div>
        <p className="mb-2 text-[15px] font-medium text-texto">¿Quién responde la encuesta?</p>
        <p className="mb-2 text-sm text-texto-suave">En educación inicial y primaria responde el representante.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            { v: 'estudiante', t: 'El estudiante' },
            { v: 'representante', t: 'Su representante (madre, padre o responsable)' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setDatos((d) => ({ ...d, informante: o.v }))}
              className={`objetivo-tactil foco-visible rounded-[var(--radius-rumbo)] border px-3.5 py-2.5 text-left text-sm ${
                datos.informante === o.v ? 'border-azul bg-azul-suave font-medium text-azul' : 'border-borde-fuerte text-texto'
              }`}
            >
              {o.t}
            </button>
          ))}
        </div>
      </div>

      {datos.informante === 'representante' && (
        <div>
          <p className="mb-2 text-[15px] font-medium text-texto">Parentesco con el estudiante</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {['madre', 'padre', 'abuelo', 'tio', 'otro'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDatos((d) => ({ ...d, parentesco: p }))}
                className={`objetivo-tactil foco-visible rounded-[var(--radius-rumbo)] border px-2 py-2 text-sm capitalize ${
                  datos.parentesco === p ? 'border-azul bg-azul-suave font-medium text-azul' : 'border-borde-fuerte'
                }`}
              >
                {p === 'tio' ? 'Tía/o' : p === 'abuelo' ? 'Abuela/o' : p}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Caso especial del paso Gasto: en vez de pedir el gasto mensual a mano
 * (y arriesgar que el encuestado se equivoque multiplicando), lo precalcula
 * con la fórmula (gastoIda + gastoRegreso) × díasAlMes y pregunta si el
 * estimado está bien. Si el estudiante dice que no (ej. tiene semanas con
 * jornadas diferentes), se habilita el input para editar a mano.
 * Antes usaba díasSemana × 4,33 pero ignoraba el input de días al mes del
 * encuestado y daba promedios inflados cuando el estudiante no viaja todas
 * las semanas (vacaciones, exámenes, licencias).
 */
function CampoGastoMensual({ datos, setDatos }: { datos: Datos; setDatos: (fn: (d: Datos) => Datos) => void }) {
  const gastoIda = datos.gastoIda as { monto?: string; moneda?: Moneda } | undefined
  const gastoRegreso = datos.gastoRegreso as { monto?: string; moneda?: Moneda } | undefined
  const declarados = (datos.declarados as { gastoMensual?: { monto?: string; moneda?: Moneda }; mensualEsCorrecto?: boolean; diasMes?: number } | undefined) ?? {}
  const diasMes = Number(declarados.diasMes ?? 0)
  const montoIda = Number(gastoIda?.monto ?? 0)
  const montoRegreso = Number(gastoRegreso?.monto ?? 0)
  const monedaCompartida = gastoIda?.moneda && gastoRegreso?.moneda && gastoIda.moneda === gastoRegreso.moneda ? gastoIda.moneda : undefined
  const puedeCalcular = montoIda >= 0 && montoRegreso >= 0 && diasMes >= 1 && diasMes <= 31 && Boolean(monedaCompartida)
  const sugerido = puedeCalcular ? ((montoIda + montoRegreso) * diasMes) : null

  // Por defecto arranca marcado como correcto: usa el autocalculado. El usuario
  // dice "no" solo si tiene variabilidad real (p.ej. temporadas de vacaciones).
  const esCorrecto = declarados.mensualEsCorrecto ?? true
  const valorActual = declarados.gastoMensual ?? (sugerido != null ? { monto: sugerido.toFixed(2), moneda: monedaCompartida } : undefined)

  // Mantiene el calculado sincronizado mientras el usuario no haya marcado "no".
  useEffect(() => {
    if (!esCorrecto) return
    if (sugerido == null) return
    const esperado = { monto: sugerido.toFixed(2), moneda: monedaCompartida }
    const actual = declarados.gastoMensual
    if (actual?.monto === esperado.monto && actual?.moneda === esperado.moneda) return
    setDatos((d) => ({ ...d, declarados: { ...(d.declarados as object), gastoMensual: esperado, mensualEsCorrecto: true } }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sugerido, monedaCompartida, esCorrecto])

  if (!puedeCalcular) {
    return (
      <div className="rounded-[var(--radius-rumbo)] border border-borde p-4 text-sm text-texto-suave">
        El gasto mensual se calcula automáticamente a partir del gasto de ida, regreso y días al mes. Complete los campos
        anteriores (y que ambos gastos estén en la misma moneda) para ver la estimación.
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-[var(--radius-rumbo)] border border-borde p-4">
      <div>
        <p className="text-sm font-medium text-texto">Gasto mensual estimado</p>
        <p className="mt-1 text-2xl font-bold text-azul">
          {sugerido!.toFixed(2)} <span className="text-base font-normal text-texto-suave">{monedaCompartida}</span>
        </p>
        <p className="mt-1 text-xs text-texto-tenue">
          Cálculo: ({montoIda.toFixed(2)} + {montoRegreso.toFixed(2)}) × {diasMes} días al mes.
        </p>
      </div>
      <div>
        <p className="mb-2 text-sm font-medium text-texto">¿Es este el promedio mensual que gastas en transporte a tu lugar de estudio?</p>
        <div className="flex gap-2">
          {[
            { v: true, t: 'Sí, es correcto' },
            { v: false, t: 'No, quiero ajustarlo' },
          ].map((o) => (
            <button
              key={String(o.v)}
              type="button"
              onClick={() => setDatos((d) => ({
                ...d,
                declarados: {
                  ...(d.declarados as object),
                  mensualEsCorrecto: o.v,
                  // Al confirmar, usa el calculado; al rechazar, deja el actual
                  // (o el calculado como punto de partida) para que lo edite.
                  gastoMensual: o.v ? { monto: sugerido!.toFixed(2), moneda: monedaCompartida } : (valorActual ?? { monto: sugerido!.toFixed(2), moneda: monedaCompartida }),
                },
              }))}
              className={`objetivo-tactil foco-visible flex-1 rounded-[var(--radius-rumbo)] border px-3.5 py-2.5 text-sm ${
                esCorrecto === o.v ? 'border-azul bg-azul-suave font-medium text-azul' : 'border-borde-fuerte text-texto'
              }`}
            >
              {o.t}
            </button>
          ))}
        </div>
      </div>
      {!esCorrecto && (
        <div>
          <p className="mb-1.5 text-sm text-texto-suave">Ingrese el monto mensual real:</p>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <Entrada
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={valorActual?.monto ?? ''}
                onChange={(e) => setDatos((d) => ({
                  ...d,
                  declarados: { ...(d.declarados as object), gastoMensual: { monto: e.target.value, moneda: valorActual?.moneda ?? monedaCompartida }, mensualEsCorrecto: false },
                }))}
              />
            </div>
            <div className="w-28 shrink-0">
              <Seleccion
                aria-label="Moneda"
                value={valorActual?.moneda ?? monedaCompartida ?? 'VES'}
                onChange={(e) => setDatos((d) => ({
                  ...d,
                  declarados: { ...(d.declarados as object), gastoMensual: { monto: valorActual?.monto ?? '0', moneda: e.target.value as Moneda }, mensualEsCorrecto: false },
                }))}
              >
                <option value="VES">Bs</option>
                <option value="USD">USD</option>
              </Seleccion>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Bloque opcional al final del paso Origen: ofrece capturar un destino distinto
 * al plantel (ej. estudiante que asiste a sede alterna o va a prácticas en otra
 * parroquia). Por defecto se deja oculto — el backend deriva el destino del
 * plantel si no viene `destino` en el payload.
 */
function BloqueDestinoAlternativo({
  datos,
  setDatos,
  nombreInstitucion,
  localidadInstitucion,
}: {
  datos: Datos
  setDatos: (fn: (d: Datos) => Datos) => void
  nombreInstitucion: string
  localidadInstitucion: string
}) {
  const activo = Boolean(datos.destinoDistinto)
  const geo = (datos.destinoGeo as ValorGeo) ?? {}
  return (
    <div className="rounded-[var(--radius-rumbo)] border border-borde-fuerte p-4">
      <label className="objetivo-tactil foco-visible flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={activo}
          onChange={(e) => setDatos((d) => ({ ...d, destinoDistinto: e.target.checked }))}
          className="mt-0.5 size-5 accent-[var(--mppt-azul-marino)]"
        />
        <span className="text-sm text-texto">
          <span className="font-medium">¿Va a estudiar a otra parroquia distinta a la del plantel?</span>
          <span className="block text-texto-suave">
            Por defecto, el destino es {nombreInstitucion} ({localidadInstitucion}). Marque solo si asiste a una sede
            alterna o va a prácticas en otra parroquia.
          </span>
        </span>
      </label>
      {activo && (
        <div className="mt-4">
          <p className="mb-1.5 text-[15px] font-medium text-texto">Estado, municipio y parroquia del destino alternativo</p>
          <GeoCascada valor={geo} onCambio={(v) => setDatos((d) => ({ ...d, destinoGeo: v }))} />
        </div>
      )}
    </div>
  )
}

function PantallaCentrada({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center px-4">{children}</div>
}

function PantallaMensaje({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <PantallaCentrada>
      <Tarjeta className="max-w-md p-8 text-center">
        <LockupMPPT ancho={140} className="mx-auto mb-6" />
        <h1 className="mb-2 text-xl font-bold text-azul">{titulo}</h1>
        <p className="text-texto-suave">{texto}</p>
      </Tarjeta>
    </PantallaCentrada>
  )
}

function PantallaGracias({ folio, offline }: { folio: string; offline: boolean }) {
  return (
    <PantallaCentrada>
      <Tarjeta className="max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-exito-fondo text-exito">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="size-8" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="mb-2 text-xl font-bold text-azul">¡Gracias por responder!</h1>
        {offline ? (
          <p className="text-texto-suave">Su respuesta se guardó en este dispositivo y se enviará sola en cuanto haya señal.</p>
        ) : (
          <>
            <p className="mb-1 text-texto-suave">Su respuesta fue recibida.</p>
            <p className="font-mono text-sm text-texto-tenue">Folio {folio}</p>
          </>
        )}
      </Tarjeta>
    </PantallaCentrada>
  )
}
